import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    CircleF,
    GoogleMap,
    Libraries,
    MarkerF,
    useJsApiLoader,
} from '@react-google-maps/api'
import { Box, Typography } from '@mui/material'
import { googleMapOptions } from '../../constants/constants'
import { useTranslation } from 'react-i18next'
interface MapContainerProps {
    markerPosition?: any
    radius?: number
    zoom: number
    setZoom?: any
    editable?: boolean
    updatedSetMarkerPosition?: (position: any) => void
}

const libraries: Libraries = ['places']

const MapContainer = (props: MapContainerProps) => {
    const { t } = useTranslation()
    const {
        markerPosition,
        radius = 0,
        zoom,
        setZoom,
        editable = false,
        updatedSetMarkerPosition,
    } = props
    const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries,
    })
    const [updatedMarkerPosition, setUpdatedMarkerPosition] =
        useState<any>(markerPosition)

    const mapRef = useRef<google.maps.Map | null>(null)

    const onLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map
    }, [])

    const onUnmount = useCallback(() => {
        mapRef.current = null
    }, [])

    const handleZoomChanged = () => {
        if (mapRef.current) {
            const newZoom = mapRef.current.getZoom()
            setZoom?.(newZoom)
        }
    }

    useEffect(() => {
        setUpdatedMarkerPosition(markerPosition)
    }, [markerPosition])

    const fitMapToBounds = useCallback(() => {
        if (mapRef.current && updatedMarkerPosition && radius > 0) {
            const bounds = new google.maps.LatLngBounds()
            const radiusInDegrees = radius / 111320 // Convert meters to degrees (approximate)
            bounds.extend(
                new google.maps.LatLng(
                    updatedMarkerPosition.lat + radiusInDegrees,
                    updatedMarkerPosition.lng + radiusInDegrees,
                ),
            )
            bounds.extend(
                new google.maps.LatLng(
                    updatedMarkerPosition.lat - radiusInDegrees,
                    updatedMarkerPosition.lng - radiusInDegrees,
                ),
            )
            mapRef.current.fitBounds(bounds)
        }
    }, [updatedMarkerPosition, radius])

    useEffect(() => {
        fitMapToBounds()
    }, [fitMapToBounds])

    return isLoaded ? (
        <Box sx={{ width: '100%', height: '100%' }}>
            <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={
                    updatedMarkerPosition || {
                        lat: 36.778259,
                        lng: -119.417931,
                    }
                }
                zoom={zoom}
                onLoad={onLoad}
                onUnmount={onUnmount}
                onZoomChanged={handleZoomChanged}
                options={googleMapOptions}
            >
                {updatedMarkerPosition && (
                    <MarkerF position={updatedMarkerPosition} />
                )}
                {updatedMarkerPosition && (
                    <CircleF
                        center={updatedMarkerPosition}
                        radius={radius}
                        options={{
                            fillColor: '#F76565',
                            strokeColor: '#B42929',
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                        }}
                        draggable={editable}
                        onDragEnd={(e) => {
                            setUpdatedMarkerPosition({
                                lat: e.latLng?.lat(),
                                lng: e.latLng?.lng(),
                            })
                            updatedSetMarkerPosition?.({
                                lat: e.latLng?.lat(),
                                lng: e.latLng?.lng(),
                            })
                        }}
                    />
                )}
            </GoogleMap>
        </Box>
    ) : (
        <Typography>{t('loadingmap')}</Typography>
    )
}

const NamedMapContainer = React.memo(MapContainer)
export default NamedMapContainer
